import { Workbox } from 'workbox-window';

interface WindowWithWorkbox extends Window {
  workbox?: Workbox;
  deferredPrompt?: BeforeInstallPromptEvent;
  navigator: Navigator & {
    standalone?: boolean;
  };
}

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

declare const window: WindowWithWorkbox;

/** vite-plugin-pwa dev server exposes the SW at this URL; production uses /sw.js */
const SERVICE_WORKER_URL = import.meta.env.DEV
  ? '/dev-sw.js?dev-sw'
  : '/sw.js';

// Register service worker for PWA functionality
export const registerSW = () => {
  if ('serviceWorker' in navigator) {
    const wb = new Workbox(SERVICE_WORKER_URL);
    
    wb.addEventListener('installed', (event) => {
      console.log('Service Worker installed:', event);
      
      if (event.isUpdate) {
        // Show update available notification
        showUpdateAvailable();
      }
    });

    wb.addEventListener('controlling', () => {
      console.log('Service Worker is controlling');
      // Reload the page to ensure all tabs are using the new service worker
      window.location.reload();
    });

    wb.addEventListener('waiting', () => {
      console.log('Service Worker is waiting');
      showUpdateAvailable();
    });

    wb.register().catch((error) => {
      console.error('Service Worker registration failed:', error);
    });

    // Store workbox instance for later use
    window.workbox = wb;
  }
};

// Show update available notification
const showUpdateAvailable = () => {
  if (Notification.permission === 'granted') {
    new Notification('Update Available', {
      body: 'A new version of Hostel Manager is available. Refresh to update.',
      icon: '/icons/pwa-192x192.png',
    });
  } else {
    // Fallback to console log or custom UI notification
    console.log('Update available: A new version of Hostel Manager is ready.');
  }
};

// Skip waiting and update service worker
export const updateServiceWorker = () => {
  const wb = window.workbox;
  if (wb) {
    wb.messageSkipWaiting();
  }
};

// Check if app is running in standalone mode (installed as PWA)
export const isStandalone = (): boolean => {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone ||
    document.referrer.includes('android-app://')
  );
};

// Show install prompt
export const showInstallPrompt = () => {
  const deferredPrompt = window.deferredPrompt;
  
  if (deferredPrompt) {
    deferredPrompt.prompt();
    
    deferredPrompt.userChoice.then((choiceResult) => {
      if (choiceResult.outcome === 'accepted') {
        console.log('User accepted the install prompt');
      } else {
        console.log('User dismissed the install prompt');
      }
      window.deferredPrompt = undefined;
    });
  }
};

// Listen for install prompt
window.addEventListener('beforeinstallprompt', (e: Event) => {
  // Prevent the mini-infobar from appearing on mobile
  e.preventDefault();
  // Store the event for later use
  window.deferredPrompt = e as BeforeInstallPromptEvent;
});

// Request notification permission
export const requestNotificationPermission = async (): Promise<boolean> => {
  if ('Notification' in window) {
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }
  return false;
};

// Send notification
export const sendNotification = (title: string, options?: NotificationOptions) => {
  if (Notification.permission === 'granted') {
    return new Notification(title, {
      icon: '/icons/pwa-192x192.png',
      badge: '/icons/pwa-192x192.png',
      ...options,
    });
  }
  return null;
};

// Check if device is online
export const isOnline = (): boolean => {
  return navigator.onLine;
};

// Listen for online/offline events
export const setupConnectivityListeners = (
  onOnline: () => void,
  onOffline: () => void
) => {
  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);
  
  // Return cleanup function
  return () => {
    window.removeEventListener('online', onOnline);
    window.removeEventListener('offline', onOffline);
  };
};

// Get app version
export const getAppVersion = (): string => {
  return (window as Window & { __APP_VERSION__?: string }).__APP_VERSION__ || '1.0.0';
};

// Share content using Web Share API
export const shareContent = async (data: ShareData): Promise<boolean> => {
  if (navigator.share) {
    try {
      await navigator.share(data);
      return true;
    } catch (error) {
      console.error('Error sharing:', error);
      return false;
    }
  }
  return false;
};